/**
 * 官网门户（Portal）公开接口
 * 供前台页面读取 CMS 配置的内容，无需登录
 */
import request from '@/api/request'

/** 获取可用语言列表 */
export const getPortalLocales = () => request.get('api/portal/locales')

/**
 * 获取站长公开配置（标签名 / favicon / logo / SEO 文案）
 * @param {string} locale 语言标识
 * @param {{ reload?: boolean }} [options] reload 为 true 时附加时间戳，绕过缓存
 */
export const getPortalWebmaster = (locale, { reload = false } = {}) => {
  const params = { locale }
  if (reload) {
    params._t = Date.now()
  }
  return request.get('api/portal/webmaster', { params })
}

/**
 * 获取导航菜单树
 * @param {string} locale 语言标识
 * @param {{ reload?: boolean }} [options] reload 为 true 时附加时间戳，绕过缓存
 */
export const getPortalMenus = (locale, { reload = false } = {}) => {
  const params = { locale }
  if (reload) {
    params._t = Date.now()
  }
  return request.get('api/portal/menus', { params })
}

/**
 * 获取页脚配置（栏目、版权、备案号等）
 * @param {string} locale 语言标识
 * @param {{ reload?: boolean }} [options] reload 为 true 时附加时间戳，绕过缓存
 */
export const getPortalFooter = (locale, { reload = false } = {}) => {
  const params = { locale }
  if (reload) {
    params._t = Date.now()
  }
  return request.get('api/portal/footer', { params })
}

/**
 * 获取首页 Hero 轮播/主视觉配置
 * @param {string} locale 语言标识
 * @param {{ reload?: boolean }} [options] reload 为 true 时附加时间戳，绕过缓存
 */
export const getPortalHomeHero = (locale, { reload = false } = {}) => {
  const params = { locale }
  if (reload) {
    params._t = Date.now()
  }
  return request.get('api/portal/home-hero', { params })
}

/**
 * 获取首页模块化区块配置（产品、新闻、解决方案等）
 * @param {string} locale 语言标识
 * @param {{ reload?: boolean }} [options] reload 为 true 时附加时间戳，绕过缓存
 */
export const getPortalHome = (locale, { reload = false } = {}) => {
  const params = { locale }
  if (reload) {
    params._t = Date.now()
  }
  return request.get('api/portal/home', { params })
}

/**
 * 获取产品分类列表
 * @param {string} locale 语言标识
 * @param {{ reload?: boolean }} [options] reload 为 true 时附加时间戳，绕过缓存
 */
export const getPortalProductCategories = (locale, { reload = false } = {}) => {
  const params = { locale }
  if (reload) {
    params._t = Date.now()
  }
  return request.get('api/portal/product-categories', { params })
}

/**
 * 获取产品索引列表
 * @param {string} locale 语言标识
 * @param {string|number} [categoryId] 分类 ID，不传则返回全部
 */
export const getPortalProductIndex = (locale, categoryId) => {
  const params = { locale }
  if (categoryId != null && categoryId !== '') {
    params.categoryId = categoryId
  }
  return request.get('api/portal/product-index', { params })
}

/** 获取单个产品详情 */
export const getPortalProductDetail = (id, locale) => request.get(`api/portal/products/${id}`, { params: { locale } })

/**
 * 产品/分类混合模糊搜索
 * @param {Object} params
 * @param {string} params.keyword 搜索关键词
 * @param {string} params.locale 语言标识
 * @param {number} [params.menuId] 父菜单 ID（仅过滤产品）
 * @param {number} [params.page=0] 页码（0-based）
 * @param {number} [params.size=20] 每页条数
 */
export const searchPortalProducts = ({ keyword, locale, menuId, page = 0, size = 20 }) => {
  const params = { keyword, locale, page, size }
  if (menuId != null && menuId !== '') {
    params.menuId = menuId
  }
  return request.get('api/portal/products/search', { params })
}

/**
 * 获取内容列表（新闻、投资者公告、介绍页等）
 * @param {Object} params 查询参数，通常包含 type、menuId、locale
 */
export const getPortalContents = (params) => request.get('api/portal/contents', { params })

/** 获取单条内容详情 */
export const getPortalContentDetail = (id, locale) => request.get(`api/portal/contents/${id}`, { params: { locale } })

/** 生成滑块验证码（天爱 TAC） */
export const genPortalCaptcha = () => request.post('api/portal/captcha/gen', {})

/** 校验滑块验证码（天爱 TAC） */
export const checkPortalCaptcha = (data) => request.post('api/portal/captcha/check', data)

/** 提交联系表单 */
export const submitContact = (data) => request.post('api/portal/contact', data)

/** 页面访问上报（非卸载场景备用；主埋点见 utils/pageview.js sendBeacon） */
export const reportPageView = (data) => request.post('api/portal/pageview', data)

/**
 * 获取法律声明或隐私保护页面
 * @param {string} key 页面标识，如 privacy、legal
 */
export const getPortalLegalPage = (key, locale) =>
  request.get(`api/portal/legal/${key}`, { params: { locale } })
